import { useState } from "react";
import { motion } from "framer-motion";
import { api } from "../lib/api";

export default function SignupSection({ light }) {
  const [form, setForm] = useState({ email: "", phone: "", consent_email: false, consent_sms: false });
  const [status, setStatus] = useState({ type: "", msg: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ type: "", msg: "" });
    if (!form.email && !form.phone) {
      setStatus({ type: "error", msg: "Enter an email or phone number." });
      return;
    }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setStatus({ type: "error", msg: "Enter a valid email address." });
      return;
    }
    if (!form.consent_email && !form.consent_sms) {
      setStatus({ type: "error", msg: "Check at least one consent box." });
      return;
    }
    setLoading(true);
    try {
      await api.post("/subscribe", {
        email: form.email || null,
        phone: form.phone || null,
        consent_email: form.consent_email,
        consent_sms: form.consent_sms,
      });
      setStatus({ type: "ok", msg: "You're on the list. First to know when new pieces land." });
      setForm({ email: "", phone: "", consent_email: false, consent_sms: false });
    } catch (err) {
      setStatus({ type: "error", msg: err.response?.data?.detail || "Something went wrong. Try again." });
    } finally {
      setLoading(false);
    }
  };

  const sub = light ? "text-ll-black/60" : "text-ll-steel";
  const input = light
    ? "border border-ll-black/20 bg-white text-ll-black placeholder:text-ll-black/40 focus:border-ll-red"
    : "border border-ll-linestrong bg-ll-black placeholder:text-ll-steel/60 focus:border-ll-red";

  return (
    <section id="drop-list" data-testid="signup-section" className={light ? "bg-ll-bone text-ll-black" : "border-t border-ll-line bg-ll-coal"}>
      <div className="mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-32">
        <div className="grid gap-12 md:grid-cols-2 md:items-end">
          <div>
            <p className="font-tech text-[11px] tracking-[0.3em] text-ll-red">THE LIST</p>
            <h2 className="mt-3 font-display text-5xl leading-[0.95] tracking-wide md:text-7xl">
              FIRST ACCESS<br />TO NEW PIECES
            </h2>
            <p className={`mt-4 max-w-md text-sm ${sub}`}>
              Weekly drops move fast. Join the list and get first word on new clocks, canvas, rugs, gadgets and mirrors — plus member codes. No spam.
            </p>
          </div>
          <form onSubmit={submit} className="space-y-4" noValidate>
            <div>
              <label htmlFor="signup-email" className={`font-tech text-[11px] tracking-[0.2em] ${sub}`}>EMAIL</label>
              <input id="signup-email" data-testid="signup-email-input" type="email" value={form.email}
                     onChange={(e) => setForm({ ...form, email: e.target.value })}
                     placeholder="you@email.com"
                     className={`mt-1 w-full px-4 py-3 text-sm outline-none transition-colors duration-200 ${input}`} />
            </div>
            <div>
              <label htmlFor="signup-phone" className={`font-tech text-[11px] tracking-[0.2em] ${sub}`}>PHONE (OPTIONAL)</label>
              <input id="signup-phone" data-testid="signup-phone-input" type="tel" value={form.phone}
                     onChange={(e) => setForm({ ...form, phone: e.target.value })}
                     placeholder="+1 555 000 0000"
                     className={`mt-1 w-full px-4 py-3 text-sm outline-none transition-colors duration-200 ${input}`} />
            </div>
            <label className="flex cursor-pointer items-start gap-3">
              <input data-testid="signup-email-consent" type="checkbox" checked={form.consent_email}
                     onChange={(e) => setForm({ ...form, consent_email: e.target.checked })}
                     className="mt-0.5 h-4 w-4 accent-ll-red" />
              <span className={`text-xs ${sub}`}>I agree to receive email updates about new ETERNEL pieces.</span>
            </label>
            <label className="flex cursor-pointer items-start gap-3">
              <input data-testid="signup-sms-consent" type="checkbox" checked={form.consent_sms}
                     onChange={(e) => setForm({ ...form, consent_sms: e.target.checked })}
                     className="mt-0.5 h-4 w-4 accent-ll-red" />
              <span className={`text-xs ${sub}`}>I agree to receive SMS updates. Msg & data rates may apply. Reply STOP to opt out.</span>
            </label>
            {status.msg && (
              <p data-testid="signup-status" className={`border px-3 py-2 text-xs ${status.type === "ok" ? "border-green-700 bg-green-900/10 text-green-700" : "border-ll-red/50 bg-ll-red/10 text-ll-red"}`}>
                {status.msg}
              </p>
            )}
            <motion.button
              data-testid="signup-submit-button"
              whileTap={{ scale: 0.98 }}
              type="submit" disabled={loading}
              className="w-full bg-ll-red py-4 font-tech text-sm tracking-[0.25em] text-ll-bone transition-colors duration-200 hover:bg-ll-redhover disabled:opacity-60"
            >
              {loading ? "JOINING…" : "JOIN THE LIST"}
            </motion.button>
          </form>
        </div>
      </div>
    </section>
  );
}
